import { makeVar } from "@apollo/client";

import type { PlayerPageTab } from "@/features/player-detail/components/molecules/PlayerPageTabs/PlayerPageTabsView";
import { RecommendationsPlayerFragment } from "@/generated/graphql";
import { PlayerCardData } from "@/types/playerCardTypes";

/**
 *
 */
export interface IRecommendation {
  type: string;
  id: string;
}
/**
 *
 */
export interface IPlayer {
  type: "recommendation" | "watchList" | "roster";
  player?: PlayerCardData | RecommendationsPlayerFragment;
  infoRecommendation?: IRecommendation;
}
export const playerDetailVar = makeVar<IPlayer | undefined>(undefined);

export const watchListSelectedVar = makeVar<string | undefined>(undefined);

/**
 * One player's remembered tab.
 *
 * SE-8285: the active tab used to be stored on its own, which made it a
 * property of the APP rather than of a player — a tab chosen on one player was
 * still selected when the next player was opened. It is now stored per player,
 * so each player keeps their own choice and a player nobody has chosen a tab
 * for opens on the default.
 */
export interface PlayerTabChoice {
  /** The player the tab was chosen for. */
  readonly playerId: string;
  /** The tab that was chosen for them. */
  readonly tab: PlayerPageTab;
}

/** The tab a player opens on when no tab has been chosen for them. */
export const DEFAULT_PLAYER_TAB: PlayerPageTab = "overview";

/**
 * How many players' tabs are remembered at once.
 *
 * The memory has to span one working pass — a scout paging a sidebar list,
 * stepping into players and back out — and nothing beyond that: a choice made
 * on a player left twenty players ago is not one anybody is returning to. 20
 * comfortably covers a sidebar page and any realistic back-and-forth, while
 * bounding the variable at 20 small records for the life of the session. The
 * alternative, leaving it unbounded, grows with every player a long session
 * touches and is never reclaimed, because a module-level reactive variable
 * outlives every screen that writes to it.
 */
export const PLAYER_TAB_MEMORY_LIMIT = 20;

/**
 * Each player's remembered player-detail tab, most recently chosen first.
 *
 * Ordering is the eviction policy: a write moves that player to the front, and
 * anything past {@link PLAYER_TAB_MEMORY_LIMIT} falls off the back. So the
 * entry discarded is the one whose tab was chosen longest ago. Note that this
 * is recency of CHOICE, not of viewing — merely opening a player cannot
 * refresh their entry, because reading is a render-time derivation and must
 * not write.
 *
 * Eviction can reach a reader that is still on screen: a header left open
 * reverts to the default tab once {@link PLAYER_TAB_MEMORY_LIMIT} other
 * players receive tab choices, with nothing happening on that screen. Bounded
 * and unlikely, and the alternative is an unbounded variable, but it is the
 * one place where the ordering above is not purely a housekeeping detail.
 *
 * Treat the value as immutable. Writers replace it with a new array (see
 * {@link rememberPlayerTab}); mutating the array in place would leave
 * `useReactiveVar` holding the same reference and nothing would re-render.
 */
export const playerTabMemoryVar = makeVar<readonly PlayerTabChoice[]>([]);

/**
 * The memory that results from choosing `tab` for `playerId`.
 *
 * Pure. Returns a new array with that player at the front, any earlier entry
 * for them removed, and the oldest choices dropped past the limit.
 * @param memory - The current memory
 * @param playerId - The player the tab is being chosen for
 * @param tab - The tab being chosen
 * @returns The new memory
 */
export const rememberPlayerTab = (
  memory: readonly PlayerTabChoice[],
  playerId: string,
  tab: PlayerPageTab
): readonly PlayerTabChoice[] =>
  [
    { playerId, tab },
    ...memory.filter(choice => choice.playerId !== playerId),
  ].slice(0, PLAYER_TAB_MEMORY_LIMIT);

/**
 * Records that `tab` was chosen for `playerId`.
 *
 * The only way app code writes the memory, so a writer cannot record a tab
 * without naming the player it is about. Every call site does this BEFORE
 * navigating, which is why nothing anywhere resets the memory on a player
 * change: a reset triggered by observing the new player would run after the
 * write and undo it.
 *
 * A missing id is a no-op — with no player on screen there is nothing for a
 * tab choice to belong to.
 * @param playerId - The player the tab is chosen for
 * @param tab - The tab being chosen
 */
export const selectPlayerTab = (
  playerId: string | null | undefined,
  tab: PlayerPageTab
): void => {
  if (!playerId) return;
  playerTabMemoryVar(rememberPlayerTab(playerTabMemoryVar(), playerId, tab));
};

/**
 * The tab remembered for `playerId`, or the default when none is.
 *
 * Pure derivation, with no write.
 *
 * Note what this does during the window Apollo keeps the previous player's data
 * while the new player's query is in flight (SE-5369): it is keyed on the
 * player being RENDERED, so during that window it answers for the previous
 * player and returns the tab remembered for THEM. On the common path — a row
 * click, where no writer has named the incoming player — that means the
 * previous player's tab stays on screen for the length of the window and is
 * replaced by the new player's once the rendered id catches up.
 * @param memory - The current memory
 * @param playerId - The id of the player currently being rendered
 * @returns The tab that player should display
 */
export const resolvePlayerActiveTab = (
  memory: readonly PlayerTabChoice[],
  playerId: string | null | undefined
): PlayerPageTab =>
  (playerId != null
    ? memory.find(choice => choice.playerId === playerId)?.tab
    : undefined) ?? DEFAULT_PLAYER_TAB;

/**
 * Narrows a remembered tab to one this player actually offers.
 *
 * Manual players render only Reports & Notes, plus Financials when the flag is
 * on; nobody without the flag may sit on Financials. This is derivation rather
 * than a correcting write (SE-8285): a write would fire while the PREVIOUS
 * player is still the rendered one and overwrite a selection a quick action had
 * just made for the player being navigated to. Deriving it costs nothing to
 * persist, because it is recomputed on every render.
 * @param tab - The remembered tab
 * @param isManual - Whether the rendered player was added manually
 * @param isOwner - Whether the Financials tab is enabled for this viewer
 * @returns The tab to display
 */
export const coercePlayerActiveTab = (
  tab: PlayerPageTab,
  isManual: boolean,
  isOwner: boolean
): PlayerPageTab => {
  if (isManual) {
    const offered = tab === "reportsNotes" || (tab === "financials" && isOwner);
    return offered ? tab : "reportsNotes";
  }
  return tab === "financials" && !isOwner ? DEFAULT_PLAYER_TAB : tab;
};

/**
 * Suppresses the desktop auto-select-first-player behavior for one cycle.
 * Set to `true` before navigating to `/players` after a delete so the user
 * sees the empty "Select a player" state instead of the next player.
 */
export const suppressAutoSelectVar = makeVar(false);

/**
 * True while a player is shown in the in-modal preview overlay
 * (PlayerPreviewModal). The scout-report entry points read this to flag the
 * Select Matches screen so it omits its back arrow — navigating dismisses the
 * preview overlay, so a back arrow there would dead-end (SE-5185 follow-up).
 */
export const playerPreviewOpenVar = makeVar(false);

/**
 * One-shot signal that tells NotesSection to auto-expand the add note form.
 * Set to `true` by MainContainer when navigating with `action=create`,
 * then immediately reset to `false` after the NotesSection consumes it.
 */
export const playerAutoCreateNoteVar = makeVar(false);

/**
 * Signals that a player-detail PDF export is in progress (SE-5062).
 *
 * Set `true` at the start of the header's `handleDownloadReport` and reset in
 * its `finally` (and on nav-away). While `true`, PlayerPageTabContentContainer
 * force-mounts DataHubTab (hidden) so its spider-chart and dot-plot refs
 * register for capture — the on-demand replacement for the desktop pre-mount
 * removed in SE-5038. The header only flips this on desktop web (the download
 * button is gated to desktop web), so a truthy value implies a web export
 * context; consumers do not need to re-check the platform.
 */
export const isExportingReportVar = makeVar(false);

/**
 *
 */
export type TypeRightColumn = "note" | "recommendation";
